import type { TabDef, TipoAnalise, SubAbaDef } from "./types";
import { ordenarPorNome } from "./ordem";

/**
 * Nome de cada seção de ensaio na linguagem do laboratório: a sigla que se usa
 * na bancada, com o nome por extenso ao lado.
 *
 * Fica AQUI, num lugar só, porque esse nome aparece em quatro telas (listagem,
 * filtro, tela de resultados e fila de aprovação). Quando cada tela tinha o seu,
 * a mesma seção se chamava "AGD (Gases)" num canto e "agdGases" no outro.
 * As chaves NÃO mudam: é o que o Relatórios manda e o que está no banco.
 */
/**
 * O NOME DE CADA ENSAIO — um só, em todo o sistema.
 *
 * São os quatro nomes que o laboratório usa para pedir o ensaio, e é o que a
 * pessoa lê em qualquer tela: menu, painel, formulário, abas do laudo e coluna
 * de listagem. Antes existiam DOIS vocabulários para as mesmas quatro coisas —
 * "Gases Dissolvidos" no menu virava "Cromatografia (gases)" no formulário, e
 * "Furanos" virava "Furfuraldeído" nas abas do laudo. Quem abria a tela pela
 * primeira vez não tinha como saber que era o mesmo ensaio.
 *
 * As CHAVES não mudam: são as que o Relatórios envia na solicitação e as que
 * estão gravadas no banco (ver server/analises.ts).
 */
export const NOME_SECAO: Record<string, string> = {
  fisicoQuimica: "Físico-Químico",
  agdGases: "Gases Dissolvidos",
  pcb: "PCB",
  furfuraldeido: "Furanos",
};

/**
 * A sigla de bancada de cada ensaio.
 *
 * Fica numa tabela PRÓPRIA, e não extraída do rótulo por `split("·")`. Aquilo
 * era uma armadilha: bastava alguém tirar o "·" de um rótulo para a "sigla"
 * virar o nome inteiro — e ela é usada em etiqueta, cabeçalho de coluna e
 * badge do menu, todos lugares onde só cabem poucos caracteres.
 */
export const SIGLA_SECAO: Record<string, string> = {
  fisicoQuimica: "FQ",
  agdGases: "CR",
  pcb: "PCB",
  furfuraldeido: "2-FALL",
};

/** Sigla + nome, para onde couberem os dois ("FQ · Físico-Químico"). */
export const ROTULO_SECAO: Record<string, string> = Object.fromEntries(
  Object.keys(NOME_SECAO).map((k) => [k, `${SIGLA_SECAO[k]} · ${NOME_SECAO[k]}`]),
);

/** Só a sigla — para caber em coluna e etiqueta. */
export const sigla = (secao: string): string => SIGLA_SECAO[secao] ?? secao;

/** Só o nome — o que a pessoa lê quando há espaço para a palavra inteira. */
export const nomeSecao = (secao: string): string => NOME_SECAO[secao] ?? secao;

/**
 * OS QUATRO ENSAIOS — o submenu que desce dentro da aba "Análises".
 *
 * A ordem é a da bancada (FQ → CR → PCB → 2-FALL) e os nomes são os que o
 * laboratório usa para pedir o ensaio. As CHAVES não podem mudar: são as que o
 * Relatórios envia na solicitação e as que estão gravadas no banco (ver
 * server/analises.ts). A sigla vem de `sigla()` acima — o nome de cada seção
 * mora num lugar só, senão a mesma análise se chamaria de um jeito no menu e de
 * outro no laudo.
 */
/*
 * Os tipos de equipamento vêm do SERVIDOR de propósito: é a mesma lista que ele
 * usa para reconhecer o tipo escrito na solicitação do Relatórios. Copiar os 15
 * nomes para cá criaria duas listas que envelhecem separadas.
 */
import { TIPOS_EQUIPAMENTO } from "../server/tipos-equipamento";
import { ARRANJOS_CDC_NORMA } from "./normas-vocabulario";
export { TIPOS_EQUIPAMENTO };

/*
 * Pelo mesmo motivo, os motivos de retenção da OS vêm do servidor: é ele que
 * recusa a liberação, e uma lista copiada aqui deixaria a tela oferecer um
 * motivo que o servidor rejeita.
 */
import { TIPOS_PENDENCIA } from "../server/tipos-pendencia";
export { TIPOS_PENDENCIA };

/**
 * ORDEM ALFABÉTICA DO CADASTRO DE EQUIPAMENTOS (ver src/ordem.ts): pelo
 * proprietário e, dentro dele, pela TAG e pelo nº de série. Fica aqui, num
 * lugar só, porque vale na aba Equipamentos e nos dois seletores de vínculo
 * (configuração do laudo e lançamento de resultados).
 */
export const ORDEM_EQUIPAMENTO = ["cliente", "tag", "numero_serie"];

export const TIPOS_ANALISE: TipoAnalise[] = [
  {
    key: "fisicoQuimica",
    titulo: "Físico-Químico",
    sigla: sigla("fisicoQuimica"),
    coluna: "fisico_quimica",
    descricao: "Rigidez dielétrica, teor de água, acidez, fator de perdas",
    icone: "flask",
    tom: "green",
  },
  {
    key: "agdGases",
    titulo: "Gases Dissolvidos",
    sigla: sigla("agdGases"),
    coluna: "agd_gases",
    descricao: "Cromatografia dos gases dissolvidos no óleo (AGD)",
    icone: "trend",
    tom: "teal",
  },
  {
    key: "pcb",
    titulo: "PCB",
    sigla: sigla("pcb"),
    coluna: "pcb",
    descricao: "Bifenilas policloradas (ascarel)",
    icone: "shield",
    tom: "emerald",
  },
  {
    key: "furfuraldeido",
    titulo: "Furanos",
    sigla: sigla("furfuraldeido"),
    coluna: "furfuraldeido",
    descricao: "Furfuraldeído — envelhecimento do papel isolante",
    icone: "history",
    tom: "lime",
  },
];

/**
 * AS QUATRO SUBABAS DO CONTROLE DE SERINGAS — o submenu que desce dentro da
 * aba, do mesmo jeito que os quatro ensaios descem dentro de "Análises".
 *
 * A ORDEM É A DO TRABALHO, não a alfabética: a seringa é cadastrada, fica no
 * estoque, sai e volta. Quem abre a aba pela primeira vez lê o fluxo inteiro
 * no menu, sem precisar que alguém explique.
 *
 * Estas chaves NÃO vão para o banco — são só navegação (ao contrário das
 * chaves dos ensaios, que o Relatórios envia e ficam gravadas).
 */
export const SUBABAS_SERINGAS: SubAbaDef[] = [
  {
    key: "cadastro",
    titulo: "Cadastro",
    sigla: "CAD",
    descricao: "Bipar as seringas novas e colocá-las no estoque",
    icone: "syringe",
    tom: "green",
  },
  {
    key: "estoque",
    titulo: "Estoque",
    sigla: "EST",
    descricao: "Onde está cada seringa: no laboratório, em serviço ou com o cliente",
    icone: "box",
    tom: "teal",
  },
  {
    key: "saida",
    titulo: "Saída de seringa",
    sigla: "SAÍ",
    descricao: "Mandar seringas para uma OS da Sercamp ou para o cliente coletar",
    icone: "truck",
    tom: "emerald",
  },
  {
    key: "retorno",
    titulo: "Retorno de seringa",
    sigla: "RET",
    descricao: "Bipar o que voltou de cada saída e devolver ao estoque",
    icone: "history",
    tom: "lime",
  },
];

/**
 * AS QUATRO SUBABAS DOS EQUIPAMENTOS DE ENSAIO — o mesmo caminho das seringas
 * (cadastra, fica no estoque, sai, volta), com o mesmo submenu no menu lateral.
 *
 * O cadastro NÃO é do Laboratório: são os mesmos equipamentos da aba
 * "Equipamentos de Ensaio" da Gestão da Operação, que é a dona dos dados (ver
 * server/equipamentos-ensaio.ts). As chaves são só navegação; a Gestão da
 * Operação tem a mesma lista com os ícones dela (a tela é o mesmo arquivo).
 */
export const SUBABAS_EQUIP_ENSAIO: SubAbaDef[] = [
  {
    key: "cadastro",
    titulo: "Cadastro",
    sigla: "CAD",
    descricao: "Cadastrar o equipamento: nome, marca, modelo, série, código de barras e certificado",
    icone: "gauge",
    tom: "green",
  },
  {
    key: "estoque",
    titulo: "Estoque",
    sigla: "EST",
    descricao: "Onde está cada equipamento, a calibração e os certificados",
    icone: "box",
    tom: "teal",
  },
  {
    key: "saida",
    titulo: "Saída de equipamento",
    sigla: "SAÍ",
    descricao: "Mandar equipamentos para uma OS, para o cliente ou para calibração externa",
    icone: "truck",
    tom: "emerald",
  },
  {
    key: "retorno",
    titulo: "Retorno de equipamento",
    sigla: "RET",
    descricao: "Bipar o que voltou de cada saída e devolver ao estoque",
    icone: "history",
    tom: "lime",
  },
];

/** Todas as abas do sistema. A ordem define o menu lateral. */
export const TABS: TabDef[] = [
  {
    /*
     * PROTOCOLO (CONTROLE DE OS) — antes de "Análises" de propósito: é por aqui
     * que toda amostra entra. A solicitação (campo, Planejamento ou criada aqui)
     * fica PENDENTE até alguém conferir o que chegou e registrar a data de
     * entrada; só então ela aparece nos módulos de análise que pediu.
     * Tela própria (components/ProtocoloView.tsx) — não usa o CrudView.
     */
    key: "protocolo",
    label: "Protocolo",
    icon: "inbox",
    resource: "protocolo",
    subtitle:
      "Controle de OS: as solicitações que chegam, a conferência e a data de entrada de cada " +
      "amostra no laboratório — é a entrada que libera a OS para os ensaios.",
    fields: [],
  },
  {
    key: "analises",
    label: "Análises",
    icon: "flask",
    resource: "analises_oleo",
    subtitle:
      "O trabalho do laboratório por ensaio e por OS — quantas amostras cada uma tem, " +
      "o que já foi preenchido e o que passou do prazo.",
    // Esta aba NÃO usa o CrudView genérico: tem navegação própria em três
    // níveis (tipo de ensaio → OS → amostras) em components/AnalisesView.tsx.
    // Por isso não há `filtros` aqui: o tipo de ensaio virou subaba e a
    // situação (em aberto / todas) virou seletor da própria tela. Os `fields`
    // continuam valendo — são eles que montam o formulário de cadastro.
    singular: "análise de óleo",
    feminino: true,
    formHint:
      "Normalmente a análise CHEGA sozinha do Relatórios e entra pelo Protocolo. Amostra nova " +
      "(cliente que trouxe o frasco no balcão, por exemplo) também se cria no Protocolo.",
    grupos: [
      { key: "os", label: "De quem é a amostra", icon: "clipboard" },
      { key: "prazos", label: "Prazos combinados", icon: "history", hint: "o que vira atraso no Dashboard" },
      { key: "ensaio", label: "O que vai ser ensaiado", icon: "flask" },
      { key: "andamento", label: "Andamento", icon: "check" },
    ],
    fields: [
      {
        name: "os_numero",
        label: "OS",
        type: "text",
        required: true,
        group: "os",
        full: true,
        buscaOs: true,
        preencheDaOs: {
          cliente: "cliente",
          equipamento: "equipamento",
          solicitante: "solicitante",
        },
        hint: "Digite o número (ou o cliente) e escolha na lista — é a chave que liga a amostra ao relatório técnico",
      },
      { name: "cliente", label: "Cliente", type: "text", group: "os" },
      {
        name: "equipamento",
        label: "Equipamento",
        type: "text",
        group: "os",
        hint: "O transformador de onde o óleo saiu — é o que monta o histórico",
      },
      { name: "solicitante", label: "Solicitante", type: "text", group: "os", placeholder: "quem pediu a análise" },
      { name: "prazo_coleta", label: "Prazo de coleta", type: "date", group: "prazos" },
      {
        name: "prazo_retorno",
        label: "Prazo de retorno",
        type: "date",
        group: "prazos",
        hint: "Passou desta data sem despachar, a análise entra em “atrasadas”",
      },
      {
        group: "ensaio",
        // Aparece na listagem (o filtro em cima trabalha com este campo): a
        // coluna mostra as siglas — "FQ · CR" — e "todas" quando a OS não
        // escolheu nenhuma, que no laudo significa sair com as 4 seções.
        name: "analises_selecionadas",
        label: "Tipo de ensaio",
        type: "checklist",
        // Os rótulos vêm de ROTULO_SECAO (sigla + nome), nunca digitados aqui:
        // o nome do ensaio mora num lugar só.
        checklistOptions: Object.keys(NOME_SECAO).map((k) => ({
          value: k,
          label: ROTULO_SECAO[k]!,
        })),
        placeholder: "Nenhuma seleção mostra todas as seções no laudo",
      },
      /*
       * SITUAÇÃO, RESULTADO E PARECER SÃO LIDOS, NUNCA DIGITADOS AQUI.
       *
       * `computed: true` = aparece na LISTA e some do formulário. Não é enfeite:
       * os três são o resultado do fluxo (conclusão → diagnóstico → aprovação →
       * liberação), e o servidor deixou de aceitá-los pelo cadastro genérico
       * (ver a lista `columns` de analises_oleo em server/resources.ts).
       *
       * Enquanto eram editáveis aqui, escrever "Despachada" no formulário fazia
       * a amostra pular os quatro passos de uma vez — e a OS ficava pronta para
       * liberar sem que ensaio nenhum tivesse sido feito.
       *
       * Onde cada um se escreve de verdade:
       *   Situação   despachar a amostra (ou liberar a OS, que despacha tudo)
       *   Resultado  sai do status do diagnóstico, no despacho
       *   Parecer    a etapa de diagnóstico, nos dois cliques na linha
       */
      { name: "status", label: "Situação", type: "text", group: "andamento", computed: true },
      { name: "resultado", label: "Resultado", type: "text", group: "andamento", computed: true },
      {
        name: "parecer",
        label: "Parecer",
        type: "textarea",
        group: "andamento",
        computed: true,
        hideInTable: true,
      },
    ],
  },
  {
    key: "material",
    label: "Material do cliente",
    icon: "box",
    resource: "material_cliente",
    subtitle:
      "Remessa recebida do cliente: quantidade prevista, conferência por leitor de código de " +
      "barras e os documentos dos Correios. Dois cliques na linha abrem a conferência.",
    singular: "remessa de material",
    feminino: true,
    formHint:
      "Cadastre a caixa que chegou do cliente. A conferência item a item (bipagem) e os " +
      "documentos dos Correios ficam nos DOIS CLIQUES na linha, depois de salvar.",
    grupos: [
      { key: "remessa", label: "A remessa", icon: "box" },
      {
        key: "correios",
        label: "Destinatário da devolução",
        icon: "truck",
        hint: "sai impresso na etiqueta e na declaração de conteúdo",
      },
      { key: "registro", label: "Registro interno", icon: "users" },
    ],
    fields: [
      {
        name: "os_numero",
        label: "OS",
        type: "text",
        required: true,
        group: "remessa",
        full: true,
        // A LUPA: procura a OS no Relatórios, aqui e na Gestão da Operação e
        // preenche o resto — inclusive o endereço, que vem da última remessa
        // deste cliente (nenhum outro sistema guarda isso).
        buscaOs: true,
        preencheDaOs: {
          cliente: "cliente",
          equipamento: "equipamento",
          responsavel: "responsavel",
          dest_nome: "dest_nome",
          dest_endereco: "dest_endereco",
          dest_complemento: "dest_complemento",
          dest_bairro: "dest_bairro",
          dest_cidade: "dest_cidade",
          dest_uf: "dest_uf",
          dest_cep: "dest_cep",
          dest_telefone: "dest_telefone",
        },
        hint: "Digite o número (ou o nome do cliente) e escolha na lista — o resto se preenche sozinho",
      },
      { name: "cliente", label: "Cliente", type: "text", group: "remessa" },
      { name: "equipamento", label: "Equipamento", type: "text", group: "remessa" },
      {
        name: "quantidade_prevista",
        label: "Qtd. prevista",
        type: "number",
        group: "remessa",
        min: 0,
        step: 1,
        suffix: "un.",
        hint: "Quantos frascos/seringas o cliente disse que mandou — é contra isto que a bipagem fecha",
      },
      // Contado no banco a cada listagem (nunca guardado): contagem gravada é a
      // primeira coisa que fica errada quando alguém apaga um item pela tela.
      { name: "bipados", label: "Bipados", type: "number", computed: true },
      {
        name: "status",
        label: "Status",
        type: "status",
        group: "remessa",
        options: ["Aguardando", "Em separação", "Separado", "Enviado"],
        hints: [
          "Remessa cadastrada, ainda não começou a conferência",
          "Conferência em andamento (bipagem dos frascos e seringas)",
          "Tudo conferido — pronto para fechar a caixa",
          "Caixa despachada para o cliente",
        ],
      },
      {
        name: "correios_rastreio",
        label: "Rastreio",
        type: "text",
        hideInTable: true,
        group: "correios",
        placeholder: "ex.: AA123456789BR",
      },
      // Destinatário: é o que sai impresso na etiqueta e na declaração.
      { name: "dest_nome", label: "Destinatário", type: "text", hideInTable: true, group: "correios" },
      { name: "dest_endereco", label: "Endereço", type: "text", hideInTable: true, group: "correios" },
      { name: "dest_complemento", label: "Complemento", type: "text", hideInTable: true, group: "correios" },
      { name: "dest_bairro", label: "Bairro", type: "text", hideInTable: true, group: "correios" },
      { name: "dest_cidade", label: "Cidade", type: "text", hideInTable: true, group: "correios" },
      {
        name: "dest_uf",
        label: "UF",
        type: "text",
        hideInTable: true,
        group: "correios",
        placeholder: "SP",
      },
      { name: "dest_cep", label: "CEP", type: "text", hideInTable: true, group: "correios", placeholder: "00000-000" },
      { name: "dest_telefone", label: "Telefone", type: "text", hideInTable: true, group: "correios" },
      { name: "responsavel", label: "Responsável", type: "text", hideInTable: true, group: "registro" },
      {
        name: "observacao",
        label: "Observação",
        type: "textarea",
        hideInTable: true,
        group: "registro",
        placeholder: "Ex.: caixa chegou molhada, 2 frascos trincados na abertura",
      },
    ],
  },
  {
    key: "seringas",
    label: "Controle de Seringas",
    icon: "syringe",
    // A aba NÃO usa o CrudView genérico nem um recurso do CRUD: tem navegação
    // própria em quatro subabas (components/SeringasView.tsx) e rotas próprias
    // no servidor (/api/seringas/...). O `resource` fica com o nome do módulo
    // só para a aba ter identidade única na lista.
    resource: "seringas",
    subtitle:
      "O parque de seringas do laboratório, pelo código de barras: o que está em estoque, " +
      "o que saiu (e para onde) e o que ainda tem de voltar.",
    fields: [],
  },
  {
    key: "equipamentos_ensaio",
    label: "Equipamentos de Ensaio",
    icon: "gauge",
    // Mesmo desenho das seringas (quatro subabas, rotas próprias), com uma
    // diferença: os dados moram na Gestão da Operação — o servidor daqui só
    // repassa (/api/equipamentos-ensaio/...). A chave NÃO é "equipamentos":
    // essa aqui no Laboratório é o transformador do cliente.
    resource: "equipamentos_ensaio",
    subtitle:
      "Os equipamentos de ensaio da Sercamp — o mesmo cadastro da Gestão da Operação: " +
      "código de barras, certificado de calibração, onde está cada um e o que tem de voltar.",
    fields: [],
  },
  {
    key: "quebras",
    label: "Quebra de frasco/seringa",
    icon: "alert",
    resource: "quebras",
    subtitle:
      "Registro de perda de frasco e seringa. É daqui que saem os indicadores do Dashboard — " +
      "por tipo, por etapa e por motivo.",
    singular: "quebra de frasco/seringa",
    feminino: true,
    formHint:
      "Cada quebra registrada vira número no Dashboard (mês, tipo e etapa). " +
      "Preencher a etapa e o motivo é o que mostra ONDE se perde vidro.",
    grupos: [
      { key: "oque", label: "O que quebrou", icon: "alert", hint: "obrigatório" },
      { key: "onde", label: "Onde e por quê", icon: "truck", hint: "alimenta o indicador por etapa" },
      { key: "registro", label: "Quem registrou", icon: "users" },
    ],
    fields: [
      { name: "data", label: "Data da quebra", type: "date", required: true, group: "oque", padraoHoje: true },
      {
        name: "tipo",
        label: "Tipo",
        type: "select",
        options: ["Frasco", "Seringa"],
        required: true,
        group: "oque",
      },
      {
        name: "quantidade",
        label: "Quantidade",
        type: "number",
        required: true,
        group: "oque",
        min: 1,
        step: 1,
        suffix: "un.",
        hint: "Quantos frascos/seringas foram perdidos nesta ocorrência",
      },
      {
        name: "os_numero",
        label: "OS",
        type: "text",
        group: "onde",
        full: true,
        buscaOs: true,
        preencheDaOs: { responsavel: "responsavel" },
        hint: "Se a perda for de uma OS específica, procure aqui — some no histórico dela",
      },
      {
        name: "etapa",
        label: "Etapa",
        type: "select",
        options: ["Coleta", "Transporte", "Recebimento", "Bancada"],
        group: "onde",
        hint: "Em que ponto do caminho a quebra aconteceu",
      },
      {
        name: "motivo",
        label: "Motivo",
        type: "text",
        group: "onde",
        placeholder: "o que causou a quebra",
        suggestions: [
          "Queda no manuseio",
          "Caixa mal acondicionada",
          "Trinca no transporte",
          "Tampa mal fechada",
          "Frasco já veio danificado",
          "Choque na bancada",
        ],
        hint: "Escolha uma sugestão ou escreva do seu jeito",
      },
      {
        name: "responsavel",
        label: "Responsável",
        type: "text",
        group: "registro",
        placeholder: "quem presenciou ou registrou",
      },
      {
        name: "observacao",
        label: "Observação",
        type: "textarea",
        hideInTable: true,
        group: "registro",
        placeholder: "Detalhe o que ajuda a evitar a próxima (ex.: caixa sem divisória, coleta na chuva)",
      },
    ],
  },
  {
    key: "aprovacao",
    label: "Aprovação de relatório",
    icon: "check",
    resource: "aprovacoes_laudo",
    subtitle:
      "Diagnósticos gerados esperando o segundo olhar. Ninguém despacha o próprio laudo: " +
      "aprovar aqui é o que libera o envio ao Relatórios.",
    fields: [
      { name: "os_numero", label: "OS", type: "text", computed: true },
      { name: "cliente", label: "Cliente", type: "text", computed: true },
      { name: "equipamento", label: "Equipamento", type: "text", computed: true },
      {
        name: "status",
        label: "Situação",
        type: "status",
        options: ["Pendente", "Aprovado", "Reprovado"],
        hints: [
          "Aguardando o segundo olhar",
          "Diagnóstico conferido e liberado",
          "Devolvido ao analista com o motivo",
        ],
        computed: true,
      },
      { name: "solicitado_por", label: "Solicitado por", type: "text", computed: true },
      { name: "solicitado_em", label: "Solicitado em", type: "date", computed: true },
      { name: "avaliado_por", label: "Avaliado por", type: "text", computed: true },
      { name: "motivo", label: "Motivo", type: "textarea", computed: true, hideInTable: true },
    ],
  },
  {
    key: "historico",
    label: "Relatórios",
    icon: "history",
    resource: "historico",
    subtitle: "Laudos prontos — OS, cliente, datas, resultado e download do arquivo",
    fields: [],
  },
  {
    key: "equipamentos",
    label: "Equipamentos",
    icon: "clipboard",
    resource: "equipamentos",
    subtitle:
      "Cadastro do equipamento de onde a amostra sai (transformador, comutador, reator...). É o " +
      "vínculo com o equipamento que permite montar o HISTÓRICO — a mesma máquina analisada ao " +
      "longo do tempo.",
    singular: "equipamento",
    ordenarPor: ORDEM_EQUIPAMENTO,
    formHint:
      "O equipamento de onde a amostra sai. Preencher classe de tensão e tipo de óleo é o " +
      "que faz o diagnóstico escolher a faixa certa da norma para este equipamento.",
    grupos: [
      { key: "ident", label: "Identificação", icon: "clipboard", hint: "proprietário é obrigatório" },
      { key: "tecnico", label: "Dados de placa", icon: "scale" },
      { key: "diag", label: "O que o diagnóstico usa", icon: "flask", hint: "casa com os limites normativos" },
      { key: "extra", label: "Complemento", icon: "box" },
    ],
    fields: [
      { name: "tag", label: "TAG", type: "text", group: "ident", placeholder: "ex.: TR-01" },
      {
        name: "tipo_equipamento",
        label: "Tipo de equipamento",
        type: "select",
        // A lista vem do servidor (server/tipos-equipamento.ts) — é escolha,
        // não digitação: "trafo", "Transformador" e "TRANSFORMADOR" digitados à
        // mão viram três equipamentos diferentes no cadastro.
        options: [...TIPOS_EQUIPAMENTO],
        group: "ident",
      },
      /*
       * "cliente" é o nome da COLUNA, não o da tela.
       *
       * O rótulo virou "Proprietário" (é de quem é o equipamento, que nem sempre
       * é quem contrata o serviço). A coluna continua `cliente` porque ela é
       * chave do índice único do cadastro, do casamento automático com a análise
       * e do histórico — renomear no banco quebraria os três por um nome.
       */
      { name: "cliente", label: "Proprietário", type: "text", required: true, group: "ident" },
      {
        name: "endereco",
        label: "Endereço",
        type: "text",
        group: "ident",
        hideInTable: true,
        placeholder: "ex.: Rod. BR-364, km 12 — Cuiabá/MT",
        hint: "Onde o equipamento está instalado (o local de instalação fica em Complemento)",
      },
      { name: "fabricante", label: "Fabricante", type: "text", group: "ident" },
      { name: "numero_serie", label: "Nº de série", type: "text", group: "ident" },
      { name: "potencia_kva", label: "Potência", type: "text", group: "tecnico", suffix: "kVA" },
      { name: "tensao_superior_kv", label: "Tensão superior", type: "text", hideInTable: true, group: "tecnico", suffix: "kV" },
      { name: "tensao_inferior_v", label: "Tensão inferior", type: "text", hideInTable: true, group: "tecnico", suffix: "V" },
      { name: "impedancia_percent", label: "Impedância", type: "text", hideInTable: true, group: "tecnico", suffix: "%" },
      { name: "volume_oleo_l", label: "Volume de óleo", type: "text", hideInTable: true, group: "tecnico", suffix: "L" },
      {
        name: "tipo_oleo",
        label: "Tipo de óleo",
        type: "select",
        options: ["Mineral", "Vegetal", "Silicone"],
        hideInTable: true,
        group: "diag",
        hint: "A base de limites é só para MINERAL — vegetal e silicone saem sem diagnóstico automático",
      },
      {
        // O comutador gera acetileno em operação NORMAL: se ele se comunica com
        // o tanque principal, o limite de C2H2 sobe de 2 para 60 ppm (NBR 7274).
        // Sem esta informação o sistema não escolhe entre os dois.
        name: "arranjo_cdc",
        label: "Comutador (CDC)",
        type: "select",
        options: [...ARRANJOS_CDC_NORMA],
        hideInTable: true,
        group: "diag",
        hint: "Muda o limite de acetileno: o comutador comunicante gera C2H2 em operação normal",
      },
      {
        /*
         * LISTA FECHADA, E NÃO TEXTO LIVRE — este é o campo que mais quebrou o
         * diagnóstico em silêncio.
         *
         * As quatro opções são as COLUNAS das tabelas da NBR 10576, e o
         * diagnóstico casa o equipamento com a faixa por TEXTO EXATO
         * (server/diagnostico.ts, `combina`). Como campo de digitação, "13,8 kV"
         * — que é uma resposta certa para uma pessoa — não é igual a
         * "≤ 36,2 kV" para o sistema: a cascata para no eixo da classe, nenhum
         * ensaio recebe limite e o físico-químico sai em branco, sem erro na
         * tela. É o caso "cadastrei tudo e não funciona".
         *
         * Os textos têm de ser IDÊNTICOS (símbolo ≤, vírgula decimal, espaços)
         * aos de db/carga-nbr-10576.ts (CLASSES), aos das sugestões da aba de
         * limites e aos que server/equipamentos.ts deriva da tensão de placa
         * (classeTensaoDeKV) — mexer em um sem mexer nos outros volta a
         * desligar o diagnóstico.
         *
         * Cadastro antigo com valor fora da lista NÃO some nem é sobrescrito:
         * o formulário traz o valor gravado como opção extra marcada "fora do
         * padrão" (ver opcoesComAtual, em components/RecordForm.tsx).
         */
        name: "classe_tensao",
        label: "Classe de tensão",
        type: "select",
        options: [
          "≤ 36,2 kV",
          "> 36,2 kV e ≤ 72,5 kV",
          "> 72,5 kV e ≤ 145 kV",
          "> 145 kV",
        ],
        hint:
          "Lista fechada de propósito: é por este texto que o equipamento acha a coluna da tabela " +
          "de limites da NBR. Escrito de outro jeito (“13,8 kV”) nenhuma faixa casa e o diagnóstico " +
          "sai em branco. Opção marcada “fora do padrão” é cadastro antigo — troque pela da lista.",
        hideInTable: true,
        group: "diag",
      },
      { name: "data_fabricacao", label: "Data de fabricação", type: "text", hideInTable: true, group: "extra", placeholder: "ex.: 2015" },
      { name: "local_instalacao", label: "Local de instalação", type: "text", hideInTable: true, group: "extra" },
      // Quantas análises esse equipamento já teve — é a porta do histórico.
      { name: "analises", label: "Análises", type: "number", computed: true },
      { name: "observacao", label: "Observação", type: "textarea", hideInTable: true, group: "extra" },
    ],
  },
  {
    key: "limites",
    label: "Limites normativos",
    icon: "scale",
    resource: "limites_normativos",
    subtitle:
      "A régua do diagnóstico. Cada linha é UMA FAIXA de um parâmetro (ex.: rigidez ≥ 40 = " +
      "Normal; 30 a 40 = Atenção; < 30 = Crítico). Parâmetro sem faixa cadastrada NÃO recebe " +
      "veredito — o sistema diz 'limite não cadastrado' em vez de adivinhar. Use " +
      "“Trazer da norma (IA)” para transcrever a tabela em vez de digitar linha a linha.",
    singular: "faixa de limite",
    feminino: true,
    formHint:
      "Uma linha = UMA faixa de UM parâmetro. Ex.: rigidez ≥ 40 kV → Normal; 30 a 40 → Atenção; " +
      "abaixo de 30 → Crítico (três linhas). Faixa cadastrada à mão já nasce valendo.",
    grupos: [
      { key: "origem", label: "De onde vem", icon: "clipboard" },
      { key: "param", label: "Qual parâmetro", icon: "flask" },
      { key: "faixa", label: "A faixa e o veredito", icon: "scale", hint: "o coração da régua" },
      { key: "quando", label: "Quando esta faixa vale", icon: "check", hint: "deixe em branco para valer sempre" },
    ],
    // O que o laboratório procura aqui é "o que ainda falta conferir" — por isso
    // a situação vem antes da seção.
    filtros: [
      {
        campo: "situacao",
        label: "Situação",
        vazioLabel: "Todas",
        opcoes: [
          { valor: "Aguardando conferência", rotulo: "Aguardando conferência" },
          { valor: "Em uso", rotulo: "Em uso no diagnóstico" },
        ],
      },
      {
        campo: "secao",
        label: "Seção",
        vazioLabel: "Todas as seções",
        opcoes: Object.keys(NOME_SECAO).map((k) => ({ valor: k, rotulo: ROTULO_SECAO[k]! })),
      },
    ],
    fields: [
      {
        // Calculada no servidor a partir de `ativo`: faixa trazida pela IA
        // nasce aguardando conferência e NÃO vale no diagnóstico até alguém
        // conferir contra o documento (botão de visto na linha).
        name: "situacao",
        label: "Situação",
        type: "status",
        computed: true,
        options: ["Em uso", "Aguardando conferência"],
        hints: ["Vale no diagnóstico", "Trazida da norma — ninguém conferiu ainda"],
      },
      {
        name: "norma",
        label: "Norma",
        type: "text",
        required: true,
        group: "origem",
        placeholder: "ex.: ABNT NBR 10576",
        suggestions: ["ABNT NBR 10576", "ABNT NBR 7274", "IEC 60422", "IEEE C57.106"],
      },
      {
        name: "secao",
        label: "Seção",
        type: "select",
        required: true,
        group: "origem",
        options: Object.keys(NOME_SECAO),
        // O banco guarda a chave; quem cadastra lê sigla + nome (um lugar só).
        optionLabels: Object.keys(NOME_SECAO).map((k) => ROTULO_SECAO[k]!),
      },
      {
        name: "parametro",
        label: "Parâmetro",
        type: "text",
        required: true,
        group: "param",
        placeholder: "ex.: rigidezDieletrica",
        hint: "Chave técnica, igual à do ensaio — é por ela que o valor medido encontra esta faixa",
        suggestions: ["rigidezDieletrica", "teorAgua", "indiceNeutralizacao", "fatorPerdas", "c2h2", "ch4", "h2", "teorPcbClorado"],
      },
      { name: "rotulo", label: "Rótulo", type: "text", group: "param", placeholder: "como aparece no laudo" },
      { name: "unidade", label: "Unidade", type: "text", group: "param", placeholder: "kV, ppm, mg KOH/g" },
      {
        name: "valor_min",
        label: "De (mín.)",
        type: "number",
        group: "faixa",
        step: 0.1,
        hint: "Vazio = sem limite inferior",
      },
      {
        name: "valor_max",
        label: "Até (máx.)",
        type: "number",
        group: "faixa",
        step: 0.1,
        hint: "Vazio = sem limite superior",
      },
      {
        // A ZONA DE ALERTA desta faixa, sem precisar cadastrar outra linha: o
        // resultado que fica a esta distância do limite, do lado ainda
        // conforme, é chamado de "próximo do limite" no diagnóstico da amostra
        // (status Atenção). É em unidade do ensaio, não em porcentagem.
        name: "margem_proximidade",
        label: "Margem de proximidade",
        type: "number",
        hideInTable: true,
        group: "faixa",
        step: 0.01,
        hint: "Na unidade do ensaio (5 = 5 kV, 0,02 = 0,02 mg KOH/g). Vazio = a margem que o laboratório definiu para o ensaio. Vale só para faixas Normal.",
      },
      {
        name: "veredito",
        label: "Veredito",
        type: "status",
        required: true,
        group: "faixa",
        options: ["Normal", "Atenção", "Crítico"],
        hints: [
          "Dentro do esperado pela norma",
          "Fora do ideal — pede acompanhamento",
          "Fora do aceitável — exige providência",
        ],
      },
      {
        // O EIXO QUE ESCOLHE A TABELA por equipamento (NBR 10576): a norma dá
        // rigidez ≥40 num transformador em uso e ≥20 num disjuntor. Aceita
        // lista com "|" porque a Tabela 3 trata transformador e reator juntos.
        name: "tipo_equipamento",
        label: "Tipo de equipamento",
        type: "text",
        hideInTable: true,
        group: "quando",
        hint: "Vazio = vale para qualquer equipamento. Vários: separe com | (ex.: Transformador|Reator)",
        suggestions: ["Transformador|Reator", ...TIPOS_EQUIPAMENTO],
      },
      {
        // O arranjo do comutador (NBR 7274) — é outro eixo, e não a condição do
        // óleo. Ficavam na mesma coluna e um filtrava o outro.
        name: "arranjo",
        label: "Arranjo (CDC)",
        type: "text",
        hideInTable: true,
        group: "quando",
        hint: "Só para os limites de gases: como o comutador se comunica com o tanque",
        suggestions: [...ARRANJOS_CDC_NORMA],
      },
      {
        name: "classe_tensao",
        label: "Classe de tensão",
        type: "text",
        hideInTable: true,
        group: "quando",
        hint: "Vazio = vale para qualquer classe",
        suggestions: [
          // As QUATRO faixas da NBR 10576 — são as colunas das tabelas da base
          // de limites, e a comparação com o cadastro é por texto exato: fora
          // desta lista, nenhuma faixa casa e o ensaio fica sem limite.
          "≤ 36,2 kV",
          "> 36,2 kV e ≤ 72,5 kV",
          "> 72,5 kV e ≤ 145 kV",
          "> 145 kV",
        ],
      },
      {
        name: "tipo_oleo",
        label: "Tipo de óleo",
        type: "text",
        hideInTable: true,
        group: "quando",
        hint: "Vazio = vale para qualquer óleo",
        suggestions: ["Mineral", "Vegetal", "Silicone"],
      },
      {
        name: "condicao",
        label: "Condição do óleo",
        type: "text",
        hideInTable: true,
        group: "quando",
        placeholder: "ex.: Em serviço (preventiva)",
        hint: "É o que escolhe entre as tabelas 3, 6, 7 e 8 da NBR 10576 — precisa casar com o que a amostra informa",
        // Os MESMOS textos da carga da 10576 e do campo da amostra: a
        // comparação é exata, e uma variação de escrita faz a faixa sumir.
        suggestions: [
          "Em serviço (preventiva)",
          "Novo ou após troca de óleo",
          "Após recondicionamento (termovácuo)",
          "Após regeneração",
        ],
      },
      {
        name: "ordem",
        label: "Ordem",
        type: "number",
        hideInTable: true,
        group: "quando",
        step: 1,
        hint: "Desempate: a primeira faixa que casar é a que vale",
      },
      { name: "observacao", label: "Observação", type: "textarea", hideInTable: true, group: "quando" },
    ],
  },
  {
    // Só ADMIN vê esta aba (o App filtra) — e o servidor recusa quem não for.
    key: "permissoes",
    label: "Acesso e permissões",
    icon: "shield",
    resource: "permissoes_usuario",
    subtitle:
      "Quem pode DIAGNOSTICAR e quem pode LIBERAR os resultados ao cliente. Nenhuma das duas é " +
      "automática: usuário novo entra sem acesso a essas etapas.",
    fields: [],
  },
  {
    key: "criterios",
    label: "Critérios do diagnóstico",
    icon: "clipboard",
    resource: "criterios_diagnostico",
    subtitle:
      "As REGRAS que a IA segue ao propor o diagnóstico da amostra — escritas em português, do " +
      "jeito do laboratório. Nasce vazia de propósito: enquanto não houver critério cadastrado, a " +
      "sugestão sai só dos valores medidos e dos limites normativos, e a tela avisa isso.",
    singular: "critério do diagnóstico",
    formHint:
      "Escreva a regra como você explicaria para um colega novo: o que olhar, em que faixa, e o " +
      "que concluir. É este texto que a IA recebe — quanto mais claro, mais previsível a sugestão.",
    grupos: [
      { key: "oque", label: "A regra", icon: "clipboard", hint: "título e texto são obrigatórios" },
      { key: "quando", label: "Quando ela vale", icon: "check", hint: "deixe a seção em branco para valer sempre" },
    ],
    filtros: [
      {
        campo: "secao",
        label: "Ensaio",
        vazioLabel: "Todos os ensaios",
        opcoes: Object.keys(NOME_SECAO).map((k) => ({ valor: k, rotulo: ROTULO_SECAO[k]! })),
      },
    ],
    fields: [
      {
        name: "titulo",
        label: "Título",
        type: "text",
        required: true,
        group: "oque",
        full: true,
        placeholder: "ex.: Acetileno presente com etileno alto",
        hint: "Como esta regra é chamada no laboratório",
      },
      {
        name: "texto",
        label: "A regra",
        type: "textarea",
        required: true,
        group: "oque",
        placeholder:
          "ex.: Se houver C2H2 acima de 1 ppm junto de C2H4 acima de 50 ppm, indicar descarga de " +
          "alta energia, recomendar inspeção interna e reamostrar em 30 dias.",
        hint: "É este texto que a IA recebe como regra a seguir",
      },
      {
        name: "secao",
        label: "Ensaio",
        type: "select",
        group: "quando",
        options: ["", ...Object.keys(NOME_SECAO)],
        optionLabels: ["Todos os ensaios", ...Object.keys(NOME_SECAO).map((k) => ROTULO_SECAO[k]!)],
        hint: "Em branco, a regra vale para qualquer ensaio da amostra",
      },
      {
        name: "ordem",
        label: "Ordem",
        type: "number",
        group: "quando",
        step: 1,
        hint: "A ordem em que as regras são apresentadas à IA",
      },
      {
        name: "observacao",
        label: "Observação",
        type: "textarea",
        hideInTable: true,
        group: "quando",
        placeholder: "De onde veio esta regra (norma, decisão interna, cliente específico)",
      },
    ],
  },
  {
    key: "razoes",
    label: "Razões NBR 7274",
    icon: "scale",
    resource: "regras_razoes_gases",
    subtitle:
      "A tabela de códigos da NBR 7274: cada linha é UM caso de falha com as faixas das três " +
      "razões entre gases (C₂H₂/C₂H₄, CH₄/H₂, C₂H₄/C₂H₆). O sistema faz a conta com os gases " +
      "medidos e procura aqui o que ela significa. Faixa deixada em branco = essa razão não " +
      "importa para aquele caso. Sem tabela cadastrada, o laudo mostra as razões calculadas e " +
      "diz que a interpretação é do analista — nunca chuta o caso mais parecido.",
    singular: "linha da tabela 7274",
    feminino: true,
    formHint:
      "Uma linha = UMA condição da NBR 7274. Preencha só as razões que importam para ela: " +
      "faixa em branco significa “NS — esta razão não entra na decisão”. O sistema só enquadra " +
      "quando as TRÊS razões cabem, ao mesmo tempo, na mesma linha. Texto no laudo e prazo de " +
      "reamostragem de cada condição se editam aqui, sem mexer no sistema.",
    grupos: [
      { key: "caso", label: "O caso de falha", icon: "alert" },
      { key: "razoes", label: "As três razões entre gases", icon: "scale", hint: "em branco = não importa" },
      { key: "origem", label: "De onde veio", icon: "clipboard" },
    ],
    filtros: [
      {
        campo: "situacao",
        label: "Situação",
        vazioLabel: "Todas",
        opcoes: [
          { valor: "Aguardando conferência", rotulo: "Aguardando conferência" },
          { valor: "Em uso", rotulo: "Em uso no diagnóstico" },
        ],
      },
    ],
    fields: [
      {
        name: "situacao",
        label: "Situação",
        type: "status",
        computed: true,
        options: ["Em uso", "Aguardando conferência"],
        hints: ["Vale no diagnóstico", "Cadastrada, mas ninguém conferiu ainda"],
      },
      {
        name: "caso",
        label: "Caso",
        type: "text",
        required: true,
        group: "caso",
        placeholder: "ex.: D1",
        suggestions: ["PD", "D1", "D2", "T1", "T2", "T3"],
        hint: "O código da tabela da norma",
      },
      {
        name: "rotulo",
        label: "Significa",
        type: "text",
        required: true,
        group: "caso",
        placeholder: "ex.: Descargas parciais",
        hint: "O nome da condição (ex.: Descargas parciais)",
      },
      { name: "r1_min", label: "C₂H₂/C₂H₄ de", type: "number", group: "razoes", step: 0.1, hint: "vazio = sem limite" },
      { name: "r1_max", label: "C₂H₂/C₂H₄ até", type: "number", group: "razoes", step: 0.1, hint: "vazio = sem limite" },
      { name: "r2_min", label: "CH₄/H₂ de", type: "number", group: "razoes", step: 0.1, hint: "vazio = sem limite" },
      { name: "r2_max", label: "CH₄/H₂ até", type: "number", group: "razoes", step: 0.1, hint: "vazio = sem limite" },
      { name: "r3_min", label: "C₂H₄/C₂H₆ de", type: "number", group: "razoes", step: 0.1, hint: "vazio = sem limite" },
      { name: "r3_max", label: "C₂H₄/C₂H₆ até", type: "number", group: "razoes", step: 0.1, hint: "vazio = sem limite" },
      {
        name: "norma",
        label: "Norma",
        type: "text",
        hideInTable: true,
        group: "origem",
        placeholder: "ex.: ABNT NBR 7274",
        suggestions: ["ABNT NBR 7274", "IEC 60599"],
      },
      { name: "revisao", label: "Revisão", type: "text", hideInTable: true, group: "origem", placeholder: "ex.: 2026" },
      {
        name: "ordem",
        label: "Ordem",
        type: "number",
        hideInTable: true,
        group: "origem",
        step: 1,
        hint: "Só organiza a lista — se as três relações couberem em duas linhas, o sistema não escolhe",
      },
      {
        name: "texto_laudo",
        label: "Texto no laudo",
        type: "textarea",
        group: "caso",
        placeholder: "ex.: Os resultados indicam descargas parciais.",
        hint: "A frase exata que sai no parecer quando esta classe for o diagnóstico",
      },
      {
        name: "reamostragem_meses",
        label: "Recoletar em (meses)",
        type: "number",
        group: "caso",
        step: 1,
        hint: "Vira “Recomenda-se a próxima reamostragem no período de X meses” quando esta for a condição enquadrada. Mude aqui — vale para as próximas gerações",
      },
      { name: "observacao", label: "Observação", type: "textarea", hideInTable: true, group: "origem" },
    ],
  },

  /**
   * ANÁLISE DE EMMS — os equipamentos de medição do PRÓPRIO laboratório.
   *
   * Substitui a planilha de controle de calibração que o laboratório mantinha à
   * mão. Não confundir com a aba "Equipamentos", logo acima: aquela é o
   * transformador do cliente, de onde a amostra sai; esta é a micropipeta, a
   * balança e o alicate com que o laboratório mede.
   *
   * As três últimas colunas — dias para vencer, ação e status — são CALCULADAS
   * pelo servidor a cada leitura, nunca digitadas. Por isso aparecem na lista e
   * somem do formulário (`computed`).
   */
  {
    key: "emms",
    label: "Análise de EMMS",
    icon: "scale",
    resource: "emms",
    singular: "equipamento de medição",
    // Pelo código (a identificação do equipamento), A→Z — a mesma ordem da
    // planilha exportada (server/emms.ts, ORDER BY codigo).
    ordenarPor: ["codigo"],
    subtitle:
      "Os equipamentos de medição do laboratório (micropipeta, balança, alicate, banho…) e o " +
      "controle de calibração de cada um. O sistema calcula sozinho quantos dias faltam, se a " +
      "calibração está válida e o que fazer — AGENDAR entra faltando 60 dias.",
    formHint:
      "O código identifica o equipamento e não se repete. Preenchendo a última calibração e a " +
      "periodicidade, a próxima data é sugerida — confira antes de salvar, porque nem sempre é a " +
      "conta exata (certificado com validade própria, por exemplo).",
    planilha: "emms",
    filtros: [
      {
        campo: "situacao",
        label: "Situação",
        opcoes: [
          { valor: "Vigente", rotulo: "Vigentes" },
          { valor: "Fora de uso", rotulo: "Fora de uso" },
        ],
        vazioLabel: "Todos",
        padrao: "Vigente",
      },
      {
        campo: "status_calibracao",
        label: "Calibração",
        opcoes: [
          { valor: "VÁLIDA", rotulo: "Válidas" },
          { valor: "VENCIDA", rotulo: "Vencidas" },
          { valor: "SEM DATA", rotulo: "Sem data" },
          { valor: "NÃO SE FAZ NECESSÁRIO", rotulo: "Não se aplica" },
        ],
        vazioLabel: "Todas",
      },
    ],
    grupos: [
      { key: "equip", label: "Equipamento", icon: "clipboard", hint: "código é obrigatório" },
      { key: "avaliacao", label: "Dados de avaliação", icon: "scale" },
      { key: "calib", label: "Calibração", icon: "history", hint: "é daqui que sai o alerta de vencimento" },
    ],
    fields: [
      /* ---------------- Equipamento ---------------- */
      {
        name: "codigo",
        label: "Código",
        type: "text",
        required: true,
        group: "equip",
        placeholder: "ex.: PIPE-04-L",
      },
      {
        name: "descricao",
        label: "Descrição: nome; modelo; marca; nº série",
        type: "textarea",
        group: "equip",
        placeholder: "Micropipeta automática; P100; Gilson; LD10365",
      },
      {
        name: "departamento",
        label: "Departamento",
        type: "select",
        group: "equip",
        // A→Z (ver src/ordem.ts); "Outros" fica por último de propósito — é a
        // opção "nenhum dos anteriores".
        options: [
          ...ordenarPorNome(
            ["PCB", "Físico-Químico", "Campo", "Amostragem-Campo", "Químico-Analítico", "Produção"],
            (d) => d,
          ),
          "Outros",
        ],
      },
      {
        name: "especificacao_tecnica",
        label: "Especificações técnicas",
        type: "text",
        group: "equip",
        hideInTable: true,
        placeholder: "ex.: ET-86",
      },
      {
        name: "situacao",
        label: "Situação",
        type: "select",
        group: "equip",
        options: ["Vigente", "Fora de uso"],
        padrao: "Vigente",
        hint: "Equipamento fora de uso não some do cadastro — sai do filtro padrão",
      },

      /* ---------------- Dados de avaliação ---------------- */
      {
        name: "envio_correio",
        label: "Pode ser enviado pelo correio?",
        type: "select",
        group: "avaliacao",
        options: ["Sim", "Não", "N.A."],
        hideInTable: true,
      },
      {
        name: "condicao_entrada",
        label: "Condição de entrada",
        type: "select",
        group: "avaliacao",
        options: ["Aprovado", "Reprovado", "Com ressalvas", "N.A."],
        hideInTable: true,
      },
      {
        name: "processo_skill",
        label: "Processo Skill?",
        type: "select",
        group: "avaliacao",
        options: ["Sim", "Não"],
        hideInTable: true,
      },
      {
        name: "escala_utilizacao",
        label: "Escala de utilização",
        type: "text",
        group: "avaliacao",
        placeholder: "ex.: 20µL - 100µL   ·   0 a 1000 ppm   ·   0,01g a 220g",
      },
      { name: "resolucao", label: "Resolução", type: "text", group: "avaliacao", placeholder: "ex.: 1µL" },
      {
        name: "funcao",
        label: "Função",
        type: "text",
        group: "avaliacao",
        placeholder: "ex.: Medir volume",
      },
      {
        name: "limite_aceitavel",
        label: "Limite aceitável",
        type: "text",
        group: "avaliacao",
        placeholder: "ex.: ± 1µL   ·   ± 10% em qualquer escala",
      },
      {
        name: "lacre",
        label: "Lacre",
        type: "select",
        group: "avaliacao",
        options: ["Sim", "Não"],
        hideInTable: true,
      },

      /* ---------------- Calibração ---------------- */
      {
        name: "exige_calibracao",
        label: "Exige calibração?",
        type: "select",
        group: "calib",
        options: ["Sim", "Não"],
        padrao: "Sim",
        hideInTable: true,
        hint: "“Não” tira o equipamento da conta de vencimento (vira “não se faz necessário”)",
      },
      {
        name: "local_armazenado",
        label: "Local armazenado",
        type: "text",
        group: "calib",
        hideInTable: true,
        placeholder: "ex.: Armário no Lab PCB",
      },
      {
        name: "periodicidade_meses",
        label: "Periodicidade (meses)",
        type: "number",
        group: "calib",
        step: 1,
        hideInTable: true,
        hint: "Usada para sugerir a próxima data",
      },
      {
        name: "justificativa_periodicidade",
        label: "Justificativa sobre a periodicidade",
        type: "text",
        group: "calib",
        hideInTable: true,
        placeholder: "ex.: Recomendação do fabricante · DOQ-CGCRE-036 · ASTM E542",
      },
      {
        name: "termo_grandeza",
        label: "Termo de descrição (grandeza)",
        type: "text",
        group: "calib",
        hideInTable: true,
        placeholder: "ex.: Volume · Massa · Temperatura",
      },
      { name: "ultima_calibracao", label: "Última calibração", type: "date", group: "calib" },
      {
        name: "proxima_calibracao",
        label: "Próxima calibração",
        type: "date",
        group: "calib",
        hint: "Sugerida pela periodicidade — pode ser corrigida",
      },
      {
        name: "observacao",
        label: "Observação",
        type: "textarea",
        group: "calib",
        hideInTable: true,
      },

      /* ------- Calculadas pelo servidor (lista, nunca formulário) ------- */
      { name: "dias_para_vencer", label: "Dias para vencer", type: "number", computed: true },
      { name: "acao_tomada", label: "Ação a ser tomada", type: "text", computed: true },
      { name: "status_calibracao", label: "Status", type: "text", computed: true },
    ],
  },
  // A aba "Usuários" foi REMOVIDA: a criação de logins é centralizada no Portal
  // SERCAMP (lá se define o usuário e quais sistemas ele pode acessar). Criar
  // login aqui gerava conta que o portão de acesso do Portal não reconhecia
  // ("login não funciona"). O acesso ao Laboratório passa a ser sempre pelo
  // card do Portal (SSO). Ver server/auth.ts (createUsuario bloqueado).
  {
    /* Aba de todos os sistemas: o pedido cai na fila do Desenvolvimento. */
    key: "solicitar_atualizacao",
    label: "Solicitar Atualização",
    icon: "clipboard",
    resource: "solicitar_atualizacao",
    subtitle:
      "Marque uma reunião com o Desenvolvimento, peça um ajuste ou avise um erro. O pedido vai direto para quem cuida dos sistemas, com o seu nome e a data.",
    fields: [],
  },
];
